// ============================================================
// Platform Control · Integrations — third-party providers wired
// into this workspace. Live data from usePlatformIntegrations()
// reads sq_integrations when deployed. The CATALOG section shows
// known providers with their capabilities and config source.
// Health check navigates to the Infrastructure screen.
// ============================================================

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePlatformIntegrations } from '../../../hooks/usePlatformIntegrations'
import ViewShell, { Card, Row, Section } from './_ViewShell'
import EmptyState from '../../../components/EmptyState'
import { SHELL_TOKENS } from '../../../shell/styles'
import { api, isAuthError } from '../../../api/client'

const KNOWN_PROVIDERS = [
  { id: 'supabase', label: 'Supabase',  powers: 'auth · database · storage · realtime', config: 'env' },
  { id: 'redis',    label: 'Redis',     powers: 'rate limiting · lockouts · session heartbeat', config: 'env' },
  { id: 'slack',    label: 'Slack',     powers: 'operator alerts · DISPATCH · approvals', config: 'env + app' },
  { id: 'github',   label: 'GitHub',   powers: 'code spine · OAuth login · Codespaces · CI/CD', config: 'env + dashboard' },
  { id: 'gitlab',   label: 'GitLab',   powers: 'client collaboration · delivery logs · tri-corder', config: 'process' },
  { id: 'sentry',   label: 'Sentry',   powers: 'browser + server observability', config: 'env' },
  { id: 'vercel',   label: 'Vercel',   powers: 'frontend delivery · deploys', config: 'env' },
  { id: 'motion',   label: 'Motion',   powers: 'scheduling · task creation · accountability', config: 'env' },
  { id: 'resend',   label: 'Resend',   powers: 'transactional email', config: 'env' },
  { id: 'gladia',   label: 'Gladia',   powers: 'voice · transcription', config: 'env' },
  { id: 'gamma',    label: 'Gamma',    powers: 'slide generation', config: 'env' },
]

interface SlackMapping {
  id: string; slack_user_id: string; slack_team_id: string | null
  supabase_user_id: string; slack_username: string | null
  slack_display_name: string | null; verified: boolean
}

export default function IntegrationsView() {
  const { integrations, loading, awaitingBackend } = usePlatformIntegrations()
  const navigate = useNavigate()

  // Slack user mappings
  const [mappings, setMappings] = useState<SlackMapping[]>([])
  const [mappingsLoading, setMappingsLoading] = useState(true)
  const [addSlackId, setAddSlackId] = useState('')
  const [addSupabaseId, setAddSupabaseId] = useState('')
  const [addUsername, setAddUsername] = useState('')
  const [addBusy, setAddBusy] = useState(false)

  const loadMappings = useCallback(async () => {
    setMappingsLoading(true)
    try {
      const r = await api.admin.slackMappings.list()
      setMappings(r.mappings as SlackMapping[])
    } catch (err) {
      if (!isAuthError(err)) console.warn('[Integrations] slack mappings:', err)
    } finally {
      setMappingsLoading(false)
    }
  }, [])

  useEffect(() => { void loadMappings() }, [loadMappings])

  async function handleAddMapping() {
    if (!addSlackId.trim() || !addSupabaseId.trim() || addBusy) return
    setAddBusy(true)
    try {
      await api.admin.slackMappings.upsert({
        slack_user_id: addSlackId.trim(),
        supabase_user_id: addSupabaseId.trim(),
        slack_username: addUsername.trim() || undefined,
        verified: true,
      })
      setAddSlackId('')
      setAddSupabaseId('')
      setAddUsername('')
      void loadMappings()
    } catch (err) {
      alert(`Failed: ${(err as Error).message}`)
    } finally {
      setAddBusy(false)
    }
  }

  async function handleDeleteMapping(id: string) {
    if (!window.confirm('Remove this Slack mapping?')) return
    try {
      await api.admin.slackMappings.delete(id)
      void loadMappings()
    } catch (err) {
      alert(`Failed: ${(err as Error).message}`)
    }
  }

  return (
    <ViewShell
      kicker="PLATFORM CONTROL · INTEGRATIONS"
      title="Integrations"
      description="Connection state, capabilities, and health for external systems. Keys stay server-side, never bundled in the browser."
      actions={
        <button
          type="button"
          onClick={() => navigate('/square/control/infrastructure')}
          style={{
            padding: '10px 16px',
            borderRadius: 10,
            border: `1px solid ${SHELL_TOKENS.border}`,
            background: 'transparent',
            color: SHELL_TOKENS.ink,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            fontFamily: 'var(--font-mono)',
            cursor: 'pointer',
          }}
        >
          Run Health Check →
        </button>
      }
    >
      <Section label="CONNECTED INTEGRATIONS">
        {loading ? (
          <EmptyState title="Loading" body="Checking integration status." />
        ) : awaitingBackend ? (
          <EmptyState
            title="Setup required"
            body="The integrations backend has not been activated yet. Contact your platform administrator to complete setup."
          />
        ) : integrations.length === 0 ? (
          <EmptyState
            title="No integrations connected"
            body="No integrations are configured yet. Integrations will appear here as providers are connected."
          />
        ) : (
          <Card>
            {integrations.map((it, i) => (
              <div
                key={it.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 0',
                  borderBottom:
                    i === integrations.length - 1
                      ? 'none'
                      : `1px solid ${SHELL_TOKENS.border}`,
                }}
              >
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: SHELL_TOKENS.ink }}>
                    {it.label}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: SHELL_TOKENS.inkMuted,
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    {it.provider} · {it.scope ?? 'workspace'}
                  </div>
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color:
                      it.status === 'connected'
                        ? SHELL_TOKENS.green
                        : it.status === 'error'
                          ? SHELL_TOKENS.red
                          : SHELL_TOKENS.inkFaint,
                    fontFamily: 'var(--font-mono)',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}
                >
                  {it.status}
                </div>
              </div>
            ))}
          </Card>
        )}
      </Section>

      <Section label="PROVIDER CATALOG">
        <Card>
          {KNOWN_PROVIDERS.map((p, i) => (
            <div
              key={p.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 0',
                borderBottom:
                  i === KNOWN_PROVIDERS.length - 1
                    ? 'none'
                    : `1px solid ${SHELL_TOKENS.border}`,
              }}
            >
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: SHELL_TOKENS.ink }}>
                  {p.label}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: SHELL_TOKENS.inkMuted,
                    fontFamily: 'var(--font-mono)',
                    marginTop: 2,
                  }}
                >
                  {p.powers}
                </div>
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: SHELL_TOKENS.inkFaint,
                  fontFamily: 'var(--font-mono)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                {p.config}
              </div>
            </div>
          ))}
        </Card>
      </Section>

      <Section label="SLACK USER MAPPINGS">
        {mappingsLoading ? (
          <EmptyState title="Loading" body="Checking Slack user mappings." />
        ) : (
          <>
            {mappings.length > 0 && (
              <Card>
                {mappings.map((m, i) => (
                  <div
                    key={m.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 0',
                      borderBottom: i === mappings.length - 1 ? 'none' : `1px solid ${SHELL_TOKENS.border}`,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: SHELL_TOKENS.ink }}>
                        {m.slack_display_name || m.slack_username || m.slack_user_id}
                      </div>
                      <div style={{ fontSize: 10, color: SHELL_TOKENS.inkMuted, fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                        {m.slack_user_id} → {m.supabase_user_id.slice(0, 8)}...
                        {m.verified && <span style={{ color: SHELL_TOKENS.green, marginLeft: 6 }}>verified</span>}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteMapping(m.id)}
                      style={{
                        padding: '5px 10px', borderRadius: 6,
                        border: `1px solid ${SHELL_TOKENS.red}40`, background: 'transparent',
                        color: SHELL_TOKENS.red, fontSize: 10, fontFamily: 'var(--font-mono)',
                        cursor: 'pointer', fontWeight: 700,
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </Card>
            )}

            {mappings.length === 0 && (
              <EmptyState
                title="No Slack mappings"
                body="Map Slack user IDs to Supabase user IDs so DISPATCH can resolve operator identity."
              />
            )}

            <div style={{
              marginTop: 12, padding: '14px 16px', borderRadius: 10,
              background: SHELL_TOKENS.surface, border: `1px solid ${SHELL_TOKENS.border}`,
            }}>
              <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: SHELL_TOKENS.inkFaint, marginBottom: 8, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700 }}>
                Add mapping
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input
                  value={addSlackId}
                  onChange={(e) => setAddSlackId(e.target.value)}
                  placeholder="Slack User ID (U01ABC...)"
                  style={fieldStyle}
                />
                <input
                  value={addSupabaseId}
                  onChange={(e) => setAddSupabaseId(e.target.value)}
                  placeholder="Supabase User UUID"
                  style={fieldStyle}
                />
                <input
                  value={addUsername}
                  onChange={(e) => setAddUsername(e.target.value)}
                  placeholder="Display name (optional)"
                  style={fieldStyle}
                />
              </div>
              <button
                type="button"
                onClick={handleAddMapping}
                disabled={!addSlackId.trim() || !addSupabaseId.trim() || addBusy}
                style={{
                  padding: '8px 14px', borderRadius: 8,
                  border: `1px solid ${SHELL_TOKENS.brandLine}`,
                  background: SHELL_TOKENS.brandSoft,
                  color: SHELL_TOKENS.brand,
                  fontSize: 11, fontWeight: 700, fontFamily: 'var(--font-mono)',
                  cursor: addBusy ? 'wait' : 'pointer',
                  opacity: (!addSlackId.trim() || !addSupabaseId.trim() || addBusy) ? 0.5 : 1,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                }}
              >
                {addBusy ? 'Adding...' : 'Add Mapping'}
              </button>
            </div>
          </>
        )}
      </Section>
    </ViewShell>
  )
}

const fieldStyle: React.CSSProperties = {
  flex: 1,
  padding: '8px 10px',
  borderRadius: 8,
  border: `1px solid ${SHELL_TOKENS.border}`,
  background: 'rgba(255,255,255,0.03)',
  color: SHELL_TOKENS.ink,
  fontSize: 11,
  fontFamily: 'var(--font-mono)',
  outline: 'none',
}
